import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, 
  CheckCircle2, 
  ArrowRight,
  FileText,
  Users,
  TrendingUp,
  Calendar,
  Clock,
  Shield,
  Star,
  Award,
  Sparkles,
  BarChart3,
  Search,
  MessageSquare,
  ExternalLink
} from "lucide-react";

const BOOKING_URL = "https://cal.com/mark-earle-6tzgnf/15min";

// Whop payment links
const WHOP_LINKS = {
  blueprint: "https://whop.com/prompt-ready/ai-visibility-blueprint",
  acceleratorMonthly: "https://whop.com/prompt-ready/the-ai-visibility-accelerator",
  acceleratorQuarterly: "https://whop.com/prompt-ready/ai-accelerator-quarterly",
  acceleratorAnnual: "https://whop.com/prompt-ready/ai-accelerator-annual"
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  const handleBuyBlueprint = () => {
    window.open(WHOP_LINKS.blueprint, "_blank");
  };

  const handleBuyAccelerator = (plan) => {
    const links = {
      monthly: WHOP_LINKS.acceleratorMonthly,
      quarterly: WHOP_LINKS.acceleratorQuarterly,
      annual: WHOP_LINKS.acceleratorAnnual
    };
    window.open(links[plan] || WHOP_LINKS.acceleratorMonthly, "_blank");
  };

  const blueprintFeatures = [
    "60-minute Strategy Session (recorded)",
    "Complete AI Visibility Audit",
    "Competitor Deep-Dive Analysis",
    "Prioritised Action Plan Document",
    "Content Gap Roadmap",
    "Review Strategy Framework",
    "Citation Building Checklist",
    "Technical SEO Recommendations",
    "30-Day Email Support",
    "Implementation Guides & Templates"
  ];

  const acceleratorFeatures = [
    "Everything in the Blueprint, plus:",
    "Full Done-For-You Implementation",
    "AI-Optimised Content Creation",
    "Service Page & FAQ Development",
    "Review Generation Campaigns",
    "Citation & Directory Submissions",
    "Schema Markup Implementation",
    "Monthly Visibility Reports",
    "Competitor Monitoring",
    "Priority Support & Slack Access",
    "Quarterly Strategy Reviews"
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0a2540] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#d4af37]" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#0a2540]">PromptReady</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12 md:py-20">
        {/* Hero */}
        <div className="text-center mb-16 animate-fade-in-up">
          <Badge className="bg-[#d4af37]/10 text-[#b5952f] border-[#d4af37]/30 mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            Exclusive Offer
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0a2540] mb-4">
            Get Found by AI.<br />Get Found by Customers.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
            When prospects ask AI "Who's the best in {`{your area}`}?" — make sure the answer is <span className="font-semibold text-[#0a2540]">you</span>.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Blueprint - DWY */}
          <Card className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg hover:shadow-xl transition-shadow overflow-hidden animate-fade-in-up">
            <CardHeader className="bg-[#0a2540] text-white p-8">
              <div className="flex items-center justify-between mb-4">
                <Badge className="bg-white/20 text-white border-0">
                  <FileText className="w-3 h-3 mr-1" />
                  Done With You
                </Badge>
              </div>
              <CardTitle className="font-heading text-2xl md:text-3xl mb-2">
                AI Visibility Blueprint
              </CardTitle>
              <p className="text-slate-300 text-sm">
                Get the complete strategy. Implement at your pace.
              </p>
              <div className="mt-6">
                <span className="text-5xl font-bold">$997</span>
                <span className="text-slate-300 ml-2">one-time</span>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <ul className="space-y-3 mb-8">
                {blueprintFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#3ab046] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={handleBuyBlueprint}
                className="w-full bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full py-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                data-testid="book-blueprint-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Get the Blueprint
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-center text-sm text-slate-500 mt-4">
                <Shield className="w-4 h-4 inline mr-1" />
                Instant access after purchase
              </p>
            </CardContent>
          </Card>

          {/* Accelerator - DFY */}
          <Card className="bg-white rounded-2xl border-2 border-[#d4af37] shadow-xl hover:shadow-2xl transition-shadow overflow-hidden relative animate-fade-in-up animate-delay-100">
            <div className="absolute top-0 right-0 bg-[#d4af37] text-[#0a2540] px-4 py-1 text-sm font-semibold rounded-bl-lg">
              MOST POPULAR
            </div>
            <CardHeader className="bg-gradient-to-br from-[#0a2540] to-[#162536] text-white p-8">
              <div className="flex items-center justify-between mb-4">
                <Badge className="bg-[#d4af37]/20 text-[#d4af37] border-0">
                  <Award className="w-3 h-3 mr-1" />
                  Done For You
                </Badge>
              </div>
              <CardTitle className="font-heading text-2xl md:text-3xl mb-2">
                AI Visibility Accelerator
              </CardTitle>
              <p className="text-slate-300 text-sm">
                We handle everything. You focus on your business.
              </p>
              <div className="mt-6">
                <span className="text-5xl font-bold">$2,500</span>
                <span className="text-slate-300 ml-2">/month</span>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <ul className="space-y-3 mb-6">
                {acceleratorFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#3ab046] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Pricing Options */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-3">
                <p className="text-sm font-semibold text-[#0a2540] mb-3">Select Your Plan:</p>
                
                <button 
                  onClick={() => setSelectedPlan('monthly')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${selectedPlan === 'monthly' ? 'bg-[#d4af37]/10 border-[#d4af37]' : 'bg-white border-slate-200 hover:border-[#d4af37]/50'}`}
                >
                  <div className="text-left">
                    <p className="font-medium text-[#0a2540]">Monthly</p>
                    <p className="text-xs text-slate-500">Flexible, cancel anytime</p>
                  </div>
                  <p className="font-semibold text-[#0a2540]">$2,500/mo</p>
                </button>

                <button 
                  onClick={() => setSelectedPlan('quarterly')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${selectedPlan === 'quarterly' ? 'bg-[#d4af37]/10 border-[#d4af37]' : 'bg-white border-slate-200 hover:border-[#d4af37]/50'}`}
                >
                  <div className="text-left">
                    <p className="font-medium text-[#0a2540]">Quarterly</p>
                    <p className="text-xs text-[#3ab046]">Save $1,500 every 90 days</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#0a2540]">$6,000</p>
                    <p className="text-xs text-slate-500">every 90 days</p>
                  </div>
                </button>

                <button 
                  onClick={() => setSelectedPlan('annual')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${selectedPlan === 'annual' ? 'bg-[#0a2540] text-white border-[#0a2540]' : 'bg-[#0a2540] text-white border-[#0a2540] hover:bg-[#162536]'}`}
                >
                  <div className="text-left">
                    <p className="font-medium">Annual</p>
                    <p className="text-xs text-[#d4af37]">Best value — Save $10,003</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">$19,997</p>
                    <p className="text-xs text-slate-300">one-time</p>
                  </div>
                </button>
              </div>

              <Button 
                onClick={() => handleBuyAccelerator(selectedPlan)}
                className="w-full bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full py-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                data-testid="book-accelerator-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Get Started — {selectedPlan === 'monthly' ? '$2,500/mo' : selectedPlan === 'quarterly' ? '$6,000' : '$19,997'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-center text-sm text-slate-500 mt-4">
                <Shield className="w-4 h-4 inline mr-1" />
                30-day performance guarantee
              </p>
            </CardContent>
          </Card>
        </div>

        {/* What You Get Section */}
        <div className="mb-16 animate-fade-in-up animate-delay-200">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0a2540] mb-3">
              What We Fix to Get You Visible
            </h2>
            <p className="text-slate-600">The key areas that determine whether AI recommends you or your competitors</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
              <div className="w-12 h-12 rounded-xl bg-[#0a2540] flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-[#d4af37]" />
              </div>
              <h3 className="font-semibold text-[#0a2540] mb-2">Authority Content</h3>
              <p className="text-sm text-slate-600">
                Service pages, FAQs, and guides that AI models cite as authoritative sources for your industry.
              </p>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
              <div className="w-12 h-12 rounded-xl bg-[#0a2540] flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-[#d4af37]" />
              </div>
              <h3 className="font-semibold text-[#0a2540] mb-2">Review Presence</h3>
              <p className="text-sm text-slate-600">
                Structured review collection across platforms that AI models use to determine reputation and trustworthiness.
              </p>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
              <div className="w-12 h-12 rounded-xl bg-[#0a2540] flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-[#d4af37]" />
              </div>
              <h3 className="font-semibold text-[#0a2540] mb-2">Citation Network</h3>
              <p className="text-sm text-slate-600">
                Directory listings, mentions, and backlinks that establish your business as a known entity to AI systems.
              </p>
            </Card>
          </div>
        </div>

        {/* Social Proof */}
        <div className="bg-[#0a2540] rounded-2xl p-8 md:p-12 text-white text-center mb-16 animate-fade-in-up animate-delay-300">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-6">
            The AI Visibility Advantage
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <p className="text-4xl font-bold text-[#d4af37] mb-2">73%</p>
              <p className="text-slate-300">of consumers now trust AI recommendations for local services</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#d4af37] mb-2">2.5x</p>
              <p className="text-slate-300">higher conversion rate from AI-referred leads vs. traditional search</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#d4af37] mb-2">89%</p>
              <p className="text-slate-300">of your competitors don't know AI visibility exists yet</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16 animate-fade-in-up animate-delay-400">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0a2540] text-center mb-10">
            Common Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="font-semibold text-[#0a2540] mb-2">How long until I see results?</h3>
              <p className="text-sm text-slate-600">
                Most clients see measurable improvements in AI visibility within 30-60 days. Full optimization typically takes 90 days as AI models update their knowledge bases.
              </p>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="font-semibold text-[#0a2540] mb-2">What if I already do SEO?</h3>
              <p className="text-sm text-slate-600">
                AI visibility is different from traditional SEO. Google rankings don't guarantee AI recommendations. We focus specifically on what AI models use to make recommendations.
              </p>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="font-semibold text-[#0a2540] mb-2">Is there a guarantee?</h3>
              <p className="text-sm text-slate-600">
                Yes. If you don't see measurable improvement in your AI visibility score within 30 days, we'll continue working at no additional cost until you do.
              </p>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="font-semibold text-[#0a2540] mb-2">What industries do you work with?</h3>
              <p className="text-sm text-slate-600">
                We specialize in high-ticket local services: roofing, solar, HVAC, plumbing, legal, dental, and similar businesses where one new customer covers the investment.
              </p>
            </Card>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center animate-fade-in-up animate-delay-500">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0a2540] mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Every day you wait, your competitors get recommended instead of you. Let's fix that.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleBuyBlueprint}
              className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full px-8 py-6"
              data-testid="final-cta-blueprint"
            >
              <FileText className="w-5 h-5 mr-2" />
              Get the Blueprint — $997
            </Button>
            <Button 
              onClick={() => handleBuyAccelerator('monthly')}
              variant="outline"
              className="border-2 border-[#0a2540] text-[#0a2540] hover:bg-[#0a2540] hover:text-white text-lg font-semibold rounded-full px-8 py-6"
              data-testid="final-cta-accelerator"
            >
              <Zap className="w-5 h-5 mr-2" />
              Done-For-You — $2,500/mo
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-lg bg-[#0a2540] flex items-center justify-center">
              <Zap className="w-4 h-4 text-[#d4af37]" />
            </div>
            <span className="font-heading text-lg font-semibold text-[#0a2540]">PromptReady</span>
          </div>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} PromptReady.io. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
